import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { api, Subsystem } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, PowerOff } from "lucide-react";
import { cn, relativeTime } from "@/lib/utils";

const stateMeta: Record<
  Subsystem["state"],
  { icon: React.ComponentType<{ className?: string }>; color: string; label: string }
> = {
  ok: { icon: CheckCircle2, color: "text-success", label: "Działa" },
  warn: { icon: AlertTriangle, color: "text-warning", label: "Ostrzeżenie" },
  down: { icon: XCircle, color: "text-destructive", label: "Awaria" },
  off: { icon: PowerOff, color: "text-muted-foreground", label: "Wyłączone" },
};

export default function Status() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["status"],
    queryFn: api.listStatus,
    refetchInterval: 30_000,
  });

  const rows = data ?? [];

  return (
    <div>
      <PageHeader
        title="Status"
        subtitle="Stan aplikacji i podsystemów"
        actions={
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={cn("mr-2 h-3.5 w-3.5", isFetching && "animate-spin")} />
            Odśwież
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border/50 text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3 text-left font-medium">Komponent</th>
                <th className="px-5 py-3 text-left font-medium">Stan</th>
                <th className="px-5 py-3 text-left font-medium">Ostatni sygnał</th>
                <th className="px-5 py-3 text-left font-medium">Uwagi</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={4} className="px-5 py-8 text-center text-sm text-muted-foreground">
                    Ładowanie…
                  </td>
                </tr>
              )}
              {rows.map((r, i) => {
                const meta = stateMeta[r.state];
                const Icon = meta.icon;
                return (
                  <motion.tr
                    key={r.name}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.02 }}
                    className="border-b border-border/30 last:border-0 hover:bg-muted/30"
                  >
                    <td className="px-5 py-3 text-sm font-medium">{r.name}</td>
                    <td className="px-5 py-3">
                      <Badge
                        variant={
                          r.state === "ok"
                            ? "success"
                            : r.state === "warn"
                            ? "warning"
                            : r.state === "down"
                            ? "destructive"
                            : "secondary"
                        }
                        className="gap-1.5"
                      >
                        <Icon className={cn("h-3 w-3", meta.color)} />
                        {meta.label}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-sm text-muted-foreground">
                      {r.last_seen ? relativeTime(r.last_seen) : "—"}
                    </td>
                    <td className="px-5 py-3 text-sm text-muted-foreground">
                      {r.last_error || r.note || "—"}
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
